import { useDatabase } from "@/hooks/useDatabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, FlaskConical, Eye } from "lucide-react";
import type { Compound } from "@/types/database";
import MoleculeViewer from "@/components/MoleculeViewer";

export default function CompoundLibrary() {
  const { database, loading } = useDatabase();
  const compounds = database?.compounds || [];
  const studies = database?.studies || [];
  const study_compounds = database?.study_compounds || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompound, setSelectedCompound] = useState<Compound | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  const handleViewStructure = (compound: Compound) => {
    setSelectedCompound(compound);
    setViewerOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-white to-[#F4F2EC]">
        <div className="container py-12">
          <Skeleton className="h-12 w-96 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filteredCompounds = compounds.filter((compound: Compound) =>
    compound.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    compound.chemical_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    compound.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCompoundStudyCount = (compoundId: string) => {
    return study_compounds.filter((sc: any) => sc.compound_id === compoundId).length;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      "Polysaccharide": "from-[#F4F2EC]0 to-[#C26A1E]",
      "Triterpene": "from-[#F4F2EC]0 to-[#C26A1E]",
      "Alkaloid": "from-[#F4F2EC]0 to-[#A98A2C]",
      "Phenolic": "from-orange-500 to-red-600",
      "Sterol": "from-[#F4F2EC]0 to-[#8A6F22]",
      "Other": "from-gray-500 to-gray-600"
    };
    return colors[category] || colors["Other"];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-white to-[#F4F2EC]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link href="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Compound Library</h1>
                <p className="text-gray-600 mt-1">{compounds.length} bioactive compounds cataloged</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-12">
        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search compounds..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Compounds Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompounds.map((compound: Compound) => {
            const studyCount = getCompoundStudyCount(compound.compound_id);
            return (
              <Card key={compound.compound_id} className="border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${getCategoryColor(compound.category)}`} />
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{compound.name}</CardTitle>
                      <CardDescription className="text-sm">{compound.chemical_name}</CardDescription>
                    </div>
                    <FlaskConical className="w-5 h-5 text-[#B45F3D]" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Category</span>
                    <span className="px-2 py-1 bg-[#F4F2EC] text-[#B45F3D] rounded text-xs font-medium">
                      {compound.category}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Studies</span>
                    <span className="text-lg font-bold text-[#B45F3D]">{studyCount}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 gap-2"
                    onClick={() => handleViewStructure(compound)}
                  >
                    <Eye className="w-4 h-4" />
                    View Structure
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredCompounds.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No compounds found matching your search.</p>
          </div>
        )}
      </div>

      {/* Molecule Viewer Modal */}
      {selectedCompound && (
        <MoleculeViewer
          compoundName={selectedCompound.name}
          chemicalName={selectedCompound.chemical_name}
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </div>
  );
}
